import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import QRScanner from "@/components/QRScanner";
import SantriSelect from "@/components/SantriSelect";
import BulkAttendance from "@/components/BulkAttendance";
import { toast } from "sonner";
import { Scan, Keyboard, ListChecks } from "@phosphor-icons/react";
import { queueOp, flushQueue } from "@/lib/offlineSync";

const STATUS = ["hadir", "terlambat", "izin", "sakit", "alfa", "berhalangan"];
const BULK_STATUS = ["hadir", "alfa", "izin", "sakit", "berhalangan"];

export default function AbsensiSantri() {
  const [tab, setTab] = useState("massal");
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [tipe, setTipe] = useState("");
  const [tanggal, setTanggal] = useState(() => new Date().toISOString().slice(0, 10));
  const [jam, setJam] = useState("");
  const [status, setStatus] = useState("hadir");
  const [scanOpen, setScanOpen] = useState(false);
  const [santri, setSantri] = useState(null);
  const [dateFilter, setDateFilter] = useState(() => new Date().toISOString().slice(0, 10));
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/absensi/santri", { params: { tanggal: dateFilter || undefined, tipe: tipe || undefined, limit: 500 } });
    setItems(data);
  };

  useEffect(() => {
    api.get("/master/absensi_type").then((r) => setTypes(r.data));
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFilter, tipe]);

  const submit = async (nisOrId) => {
    if (!tipe) return toast.error("Pilih tipe absensi terlebih dahulu");
    if (!nisOrId) return toast.error("Pilih santri dulu");
    const payload = { nis: nisOrId, tipe, tanggal, status };
    try {
      if (navigator.onLine) {
        await api.post("/absensi/santri", payload);
        toast.success(`Absensi tercatat: ${nisOrId}`);
      } else {
        await queueOp({ op: "create", path: "/absensi/santri", payload });
        toast.success(`Offline — antre untuk sync: ${nisOrId}`);
      }
      setSantri(null);
      load();
      flushQueue(api);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const onScanned = async (text) => {
    setScanOpen(false);
    try {
      const { data } = await api.get(`/santri/by-nis/${encodeURIComponent(text)}`);
      setSantri({ nis: data.nis, nama: data.nama, kamar_nama: data.kamar_nama, kelas_nama: data.kelas_nama, id: data.id });
      await submit(text);
    } catch (e) {
      toast.error("Santri tidak ditemukan: " + text);
    }
  };

  const manualSubmit = async (e) => {
    e.preventDefault();
    if (!santri) return;
    submit(santri.nis);
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Modul</div>
        <h1 className="font-display text-3xl font-bold text-[#064E3B]">Absensi Santri</h1>
        <p className="text-sm text-slate-500 mt-1">
          Dua metode: <b>Absensi Massal</b> (pilih Kamar/Kelas/Halaqah, tandai per santri) dan <b>QR/Barcode</b>.
        </p>
      </div>

      <div className="imsyaf-card p-2 flex gap-1 flex-wrap">
        <button data-testid="tab-santri-massal" onClick={() => setTab("massal")}
          className={`px-4 py-2 rounded-md text-sm font-medium ${tab === "massal" ? "bg-[#14532d] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          <ListChecks size={14} className="inline -mt-0.5 mr-1" /> Absensi Massal
        </button>
        <button data-testid="tab-santri-qr" onClick={() => setTab("qr")}
          className={`px-4 py-2 rounded-md text-sm font-medium ${tab === "qr" ? "bg-[#14532d] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          <Scan size={14} className="inline -mt-0.5 mr-1" /> QR / Barcode
        </button>
      </div>

      {tab === "massal" && (
        <div className="space-y-4">
          <div className="imsyaf-card p-4 grid md:grid-cols-3 gap-3">
            <div><label className="text-xs">Tipe Absensi *</label>
              <select data-testid="massal-santri-tipe" className="imsyaf-input" value={tipe} onChange={(e) => setTipe(e.target.value)}>
                <option value="">Pilih…</option>
                {types.map((t) => <option key={t.id} value={t.nama}>{t.nama}</option>)}
              </select>
            </div>
            <div><label className="text-xs">Tanggal</label>
              <input type="date" className="imsyaf-input" value={tanggal} onChange={(e) => setTanggal(e.target.value)} /></div>
            <div><label className="text-xs">Jam (opsional)</label>
              <input type="time" className="imsyaf-input" value={jam} onChange={(e) => setJam(e.target.value)} /></div>
          </div>
          {!tipe ? (
            <div className="imsyaf-card p-8 text-center text-slate-400">
              Pilih tipe absensi terlebih dahulu, lalu pilih Kamar/Kelas/Halaqah di bawah.
            </div>
          ) : (
            <BulkAttendance
              endpoint="/absensi/santri"
              basePayload={{ tipe, tanggal, jam }}
              statuses={BULK_STATUS}
              izinKey="izin"
              femaleOnlyStatuses={["berhalangan"]}
              onDone={load}
            />
          )}
        </div>
      )}

      {tab === "qr" && (
        <>
          <div className="imsyaf-card p-4 grid md:grid-cols-4 gap-3">
            <div><label className="text-xs">Tipe Absensi *</label>
              <select data-testid="absensi-tipe" className="imsyaf-input" value={tipe} onChange={(e) => setTipe(e.target.value)}>
                <option value="">Pilih…</option>
                {types.map((t) => <option key={t.id} value={t.nama}>{t.nama}</option>)}
              </select>
            </div>
            <div><label className="text-xs">Tanggal</label>
              <input type="date" className="imsyaf-input" value={tanggal} onChange={(e) => setTanggal(e.target.value)} /></div>
            <div><label className="text-xs">Status</label>
              <select className="imsyaf-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="absensi-status">
                {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select></div>
            <div className="flex items-end gap-2">
              <button onClick={() => setScanOpen(true)} className="imsyaf-btn-primary w-full" data-testid="scan-open-btn"><Scan size={18} /> Scan QR/Barcode</button>
            </div>
          </div>

          <form onSubmit={manualSubmit} className="imsyaf-card p-4 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[280px]">
              <label className="text-xs">Cari Santri (nama / kamar) — cadangan input manual</label>
              <SantriSelect value={santri} onChange={setSantri} testid="absensi-santri-picker" />
            </div>
            <button type="submit" disabled={!santri} className="imsyaf-btn-outline disabled:opacity-50"><Keyboard size={16} /> Catat Absensi</button>
          </form>

          {scanOpen && (
            <div className="imsyaf-card p-5">
              <QRScanner onScan={onScanned} onError={(m) => { setScanOpen(false); toast.error(m); }} />
              <div className="text-right mt-3"><button onClick={() => setScanOpen(false)} className="imsyaf-btn-outline">Tutup</button></div>
            </div>
          )}
        </>
      )}

      <div className="imsyaf-card p-4 flex items-end gap-3">
        <div><label className="text-xs">Filter Tanggal</label>
          <input type="date" className="imsyaf-input" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} /></div>
        <div className="text-sm text-slate-500 pb-2">Total: <b>{items.length}</b></div>
      </div>

      <div className="imsyaf-card overflow-hidden">
        <table className="w-full text-sm zebra">
          <thead className="bg-slate-50"><tr>
            <th className="text-left px-4 py-2 font-semibold">Waktu</th>
            <th className="text-left px-4 py-2 font-semibold">NIS</th>
            <th className="text-left px-4 py-2 font-semibold">Nama</th>
            <th className="text-left px-4 py-2 font-semibold">Tipe</th>
            <th className="text-left px-4 py-2 font-semibold">Status</th>
            <th className="text-left px-4 py-2 font-semibold">Alasan/Catatan</th>
            <th className="text-left px-4 py-2 font-semibold">Petugas</th>
            <th className="text-right px-4 py-2 font-semibold">Aksi</th>
          </tr></thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t border-slate-100" data-testid={`absensi-row-${a.id}`}>
                <td className="px-4 py-2">{a.created_at?.slice(11, 16)}</td>
                <td className="px-4 py-2 font-mono">{a.santri_nis}</td>
                <td className="px-4 py-2">{a.santri_nama}</td>
                <td className="px-4 py-2">{a.tipe}</td>
                <td className="px-4 py-2"><span className={`imsyaf-badge ${a.status === "hadir" ? "bg-green-100 text-green-800" : a.status === "terlambat" ? "bg-amber-100 text-amber-800" : a.status === "berhalangan" ? "bg-pink-100 text-pink-700" : "bg-red-100 text-red-700"}`}>{a.status}</span></td>
                <td className="px-4 py-2 text-xs text-slate-500">{a.alasan || a.catatan || "-"}</td>
                <td className="px-4 py-2 text-xs text-slate-500">{a.petugas_nama}</td>
                <td className="px-4 py-2 text-right">
                  <RowActions testid={`absensi-${a.id}`}
                    onEdit={() => setEditing({ ...a })}
                    onDelete={async () => { await api.delete(`/absensi/santri/${a.id}`); toast.success("Dihapus"); load(); }}
                    confirmText="Hapus absensi ini?" />
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Belum ada absensi hari ini.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="edit-absensi-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full space-y-3 relative">
            <button onClick={() => setEditing(null)} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"><XIcon size={18}/></button>
            <h3 className="font-display text-lg font-bold text-[#064E3B]">Edit Absensi — {editing.santri_nama}</h3>
            <div><label className="text-xs">Status</label>
              <select className="imsyaf-input" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="edit-abs-status">
                {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select></div>
            <div><label className="text-xs">Alasan</label>
              <input className="imsyaf-input" value={editing.alasan || ""} onChange={(e) => setEditing({ ...editing, alasan: e.target.value })}/></div>
            <div><label className="text-xs">Catatan</label>
              <input className="imsyaf-input" value={editing.catatan || ""} onChange={(e) => setEditing({ ...editing, catatan: e.target.value })}/></div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="imsyaf-btn-outline">Batal</button>
              <button onClick={async () => {
                try {
                  await api.put(`/absensi/santri/${editing.id}`, { status: editing.status, alasan: editing.alasan, catatan: editing.catatan });
                  toast.success("Absensi diperbarui"); setEditing(null); load();
                } catch (e) { toast.error(e.response?.data?.detail || e.message); }
              }} className="imsyaf-btn-primary" data-testid="edit-abs-save">Simpan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
